/**
 * "Beat me": the pure parts of a challenge (docs/SHARING-BUILD.md). The words for the
 * challenger, the finish's verdict, the pace ghost's maths and the sharer's feedback
 * line. No React, no storage, no clock: everything is passed in.
 */
import { answersByTime, formatDuration, type Level } from '@arrow/game-engine';

/**
 * What a challenge game carries in its session: the time to beat, and its pace.
 * Challenges are anonymous: no names anywhere, so nobody is identified by a link.
 */
export interface ChallengeContext {
	code: string;
	number: number;
	level: Level;
	/** The daily's date (`YYYY-MM-DD`). */
	date: string;
	/** The time to beat. */
	durationMs: number;
	/** The challenger's elapsed ms at each answer solve, ascending. */
	paceMs: number[];
	/**
	 * Opened on a puzzle this device had already solved: the result is settled on arrival,
	 * so there's no rematch to send (QA2-2).
	 */
	settled?: boolean;
}

export interface Verdict {
	/** You were faster (strictly), as the api counts it. */
	beat: boolean;
	/** Exactly the same time. */
	tie: boolean;
	/** |yours − theirs|, ms. */
	marginMs: number;
	/** "You won by 0:38", "Your challenger won by 0:12" (one line on the poster at 360px). */
	text: string;
}

/** The finish of a challenge: who won, and by how much. */
export function verdict(myMs: number, theirMs: number): Verdict {
	const margin = Math.abs(Math.round(myMs) - Math.round(theirMs));
	const beat = myMs < theirMs;
	const tie = Math.round(myMs) === Math.round(theirMs);
	const by = margin < 1000 ? 'by a whisker' : `by ${formatDuration(margin)}`;
	const text = tie ? 'Dead heat with your challenger' : beat ? `You won ${by}` : `Your challenger won ${by}`;
	return { beat, tie, marginMs: margin, text };
}

/** How the pace ghost names the other side: never a name. */
export const CHALLENGER = 'Your challenger';

export type PaceState = 'ahead' | 'level' | 'behind' | 'done';

export interface PaceView {
	/** Your solved answers. */
	mine: number;
	/** The challenger's answers at the same elapsed time. */
	theirs: number;
	total: number;
	/** 0–1 along the track. */
	minePos: number;
	theirsPos: number;
	state: PaceState;
	/** "Your challenger had 9 by now. You’re ahead." (one line under the track, so no "answers") */
	note: string;
}

/**
 * The pace ghost at `elapsed` ms: your answers against the challenger's at the same
 * point in their solve. Past their finish it says their time instead.
 */
export function paceView(input: {
	mine: number;
	total: number;
	paceMs: readonly number[];
	elapsedMs: number;
	durationMs: number;
}): PaceView {
	const total = Math.max(1, input.total, input.mine);
	const finished = input.elapsedMs >= input.durationMs && input.durationMs > 0;
	// Their count: from the pace when there is one, else (an old solve) done only at the end.
	const theirs = Math.min(
		total,
		input.paceMs.length > 0 ? answersByTime(input.paceMs, input.elapsedMs) : finished ? total : 0,
	);
	const mine = Math.min(total, Math.max(0, input.mine));
	const state: PaceState = finished ? 'done' : mine > theirs ? 'ahead' : mine === theirs ? 'level' : 'behind';
	const note =
		state === 'done'
			? `${CHALLENGER} finished in ${formatDuration(input.durationMs)}.`
			: `${CHALLENGER} had ${theirs} by now. ${state === 'ahead' ? 'You’re ahead.' : state === 'level' ? 'Neck and neck.' : 'You’re behind.'}`;
	return { mine, theirs, total, minePos: mine / total, theirsPos: theirs / total, state, note };
}

export interface ResultsLike {
	played: number;
	/** Completed faster than you (the api's count; else counted from the entries). */
	beat?: number;
	entries: { durationMs: number; beat: boolean }[];
}

/**
 * The sharer's next visit: "Yesterday’s challenge: 3 friends played. 1 beat your time."
 * Null when nobody has played yet. Anonymous: counts, never names.
 */
export function feedbackLine(results: ResultsLike, myMs: number, when: 'today' | 'yesterday'): { lead: string; rest: string } | null {
	if (!(results.played > 0)) return null;
	const lead = `${when === 'today' ? 'Today' : 'Yesterday'}’s challenge:`;
	const played = `${results.played} ${results.played === 1 ? 'friend' : 'friends'} played.`;
	const beat = typeof results.beat === 'number' ? results.beat : results.entries.filter((e) => e.beat).length;
	const tail =
		beat > 0
			? `${beat} beat your time.`
			: results.entries.length > 0
				? `Nobody beat your ${formatDuration(myMs)}.`
				: 'Nobody has finished yet.';
	return { lead, rest: `${played} ${tail}` };
}
